import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAppStore } from '../stores/app'
import { createContentTask, cancelContentTask, rerunContentTask, getContentTask, saveContentVersion, ApiError } from '../lib/api'
import { getErrorMessage } from '../lib/errors'
import { toast } from 'sonner'
import { useRef, useEffect } from 'react'

export interface ContentTask {
  id: string
  organization_id: string
  persona_id: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  content_type: string
  channel: string
  topic: string
  brief: string | null
  keywords: string[] | null
  word_count_target: number | null
  include_research: boolean | null
  research_query: string | null
  created_at: string
  started_at: string | null
  completed_at: string | null
  error_message: string | null
  conversation_id?: string | null
}

/** Single source/citation for a content piece (research, references). */
export interface ContentSource {
  id: number
  title: string
  url: string
  domain?: string
  snippet?: string
}

export interface ContentPiece {
  id: string
  task_id: string
  persona_id: string | null
  title: string | null
  body: string
  excerpt: string | null
  cta: string | null
  status: string | null
  current_version: number | null
  word_count: number | null
  created_at: string
  updated_at: string
  sources?: ContentSource[]
  research_summary?: string
  research_key_findings?: string[]
}

interface ContentTaskCreate {
  persona_id: string
  content_type: string
  channel: string
  topic: string
  brief?: string
  keywords?: string[]
  word_count_target?: number
  include_research?: boolean
  research_query?: string
}

export function useContentTasks(filters?: { status?: string; persona_id?: string }) {
  const organizationId = useAppStore((state) => state.organizationId)
  const queryClient = useQueryClient()
  const previousTasksRef = useRef<Record<string, string>>({})

  const queryKey = ['content_tasks', organizationId, filters]
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!organizationId) return []
      
      let query = supabase
        .from('content_tasks')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })

      if (filters?.status) {
        query = query.eq('status', filters.status)
      }
      if (filters?.persona_id) {
        query = query.eq('persona_id', filters.persona_id)
      }

      const { data, error } = await query

      if (error) throw error
      return data as ContentTask[]
    },
    enabled: !!organizationId,
    refetchInterval: (query) => {
      const data = query.state.data
      const tasks = Array.isArray(data) ? data : []
      const hasPendingOrRunning = tasks.some(
        (task: ContentTask) => task.status === 'pending' || task.status === 'running'
      )
      return hasPendingOrRunning ? 5000 : false
    },
  })

  // Detect task completion and show notifications
  useEffect(() => {
    const tasks = Array.isArray(query.data) ? query.data : []
    if (tasks.length > 0) {
      tasks.forEach((task: ContentTask) => {
        const previousStatus = previousTasksRef.current[task.id]
        
        // Check if task just completed
        if (previousStatus === 'running' && task.status === 'completed') {
          toast.success('Contenido generado', {
            description: `La tarea "${task.topic}" se completó exitosamente`,
          })
        }
        
        // Check if task just failed
        if ((previousStatus === 'running' || previousStatus === 'pending') && task.status === 'failed') {
          toast.error('Generación fallida', {
            description: task.error_message || `La tarea "${task.topic}" falló`,
          })
        }
        
        // Update the reference
        previousTasksRef.current[task.id] = task.status
      })
    }
  }, [query.data])

  const createMutation = useMutation({
    mutationFn: async (task: ContentTaskCreate) => {
      if (!organizationId) throw new Error('No organization ID')
      
      // Call backend API which handles DB insert + Celery worker dispatch
      const response = await createContentTask(task)
      
      // Return task_id for compatibility with existing code
      return { id: response.task_id, ...task }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content_tasks', organizationId] })
      toast.success('Tarea creada exitosamente', {
        description: 'La generación de contenido ha comenzado',
      })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        if (error.status === 402) {
          toast.error('Presupuesto excedido', {
            description: error.message,
          })
        } else {
          toast.error('Error al crear la tarea', {
            description: error.message,
          })
        }
        console.error('API Error:', error.message, error.status, error.data)
      } else {
        toast.error('Error al crear la tarea', {
          description: getErrorMessage(error),
        })
        console.error('Unexpected error:', error)
      }
    },
  })

  const cancelMutation = useMutation({
    mutationFn: async (taskId: string) => {
      // Call backend API which handles cancellation + WebSocket notification
      await cancelContentTask(taskId)
      
      // Return the taskId for compatibility
      return { id: taskId, status: 'cancelled' }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content_tasks', organizationId] })
      toast.success('Tarea cancelada', {
        description: 'La tarea se ha cancelado correctamente',
      })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error('Error al cancelar la tarea', {
          description: error.message,
        })
        console.error('API Error:', error.message, error.status, error.data)
      } else {
        toast.error('Error al cancelar la tarea', {
          description: getErrorMessage(error),
        })
        console.error('Unexpected error:', error)
      }
    },
  })

  const rerunMutation = useMutation({
    mutationFn: async (taskId: string) => {
      if (!organizationId) throw new Error('No organization ID')
      
      // Call backend API to re-execute the task
      const response = await rerunContentTask(taskId)
      
      return { id: taskId, ...response }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content_tasks', organizationId] })
      toast.success('Tarea reencolada', {
        description: 'La tarea se ejecutará nuevamente',
      })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error('Error al reintentar la tarea', {
          description: error.message,
        })
        console.error('API Error:', error.message, error.status, error.data)
      } else {
        toast.error('Error al reintentar la tarea', {
          description: getErrorMessage(error),
        })
        console.error('Unexpected error:', error)
      }
    },
  })

  const updateStatusMutation = useMutation({
    mutationFn: async ({ pieceId, status }: { pieceId: string; status: string }) => {
      const { data, error } = await supabase
        .from('content_pieces')
        .update({ status })
        .eq('id', pieceId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      // Invalidate content task queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['content_task', data.task_id] })
      toast.success('Estado actualizado', {
        description: 'El estado del contenido se ha actualizado',
      })
    },
    onError: (error) => {
      toast.error('Error al actualizar el estado', {
        description: getErrorMessage(error),
      })
      console.error('Failed to update content status:', error)
    },
  })

  const saveVersionMutation = useMutation({
    mutationFn: async ({
      contentId,
      taskId,
      payload,
    }: {
      contentId: string
      taskId: string
      payload: { body: string; title?: string; excerpt?: string; cta?: string; edited_with_ai?: boolean }
    }) => {
      if (!organizationId) throw new Error('No organization ID')
      const updated = await saveContentVersion(contentId, payload)
      return { contentId, taskId, updated }
    },
    onSuccess: ({ contentId, taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['content_task', taskId] })
      queryClient.invalidateQueries({ queryKey: ['content_versions', contentId] })
      toast.success('Versión guardada', {
        description: 'El contenido se ha guardado como nueva versión',
      })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error('Error al guardar la versión', {
          description: error.message,
        })
        console.error('API Error:', error.message, error.status, error.data)
      } else {
        toast.error('Error al guardar la versión', {
          description: getErrorMessage(error),
        })
        console.error('Unexpected error:', error)
      }
    },
  })

  return {
    ...query,
    createTask: createMutation.mutateAsync,
    cancelTask: cancelMutation.mutateAsync,
    rerunTask: rerunMutation.mutateAsync,
    updateContentStatus: (pieceId: string, status: string) =>
      updateStatusMutation.mutateAsync({ pieceId, status }),
    saveContentVersion: (contentId: string, taskId: string, payload: { body: string; title?: string; excerpt?: string; cta?: string; edited_with_ai?: boolean }) =>
      saveVersionMutation.mutateAsync({ contentId, taskId, payload }),
    isSavingVersion: saveVersionMutation.isPending,
    isCreating: createMutation.isPending,
    isCancelling: cancelMutation.isPending,
    isRerunning: rerunMutation.isPending,
  }
}

export function useContentTask(taskId: string | null) {
  return useQuery({
    queryKey: ['content_task', taskId],
    queryFn: async () => {
      if (!taskId) return null

      const response = await getContentTask(taskId)
      const { tarea, piezas_contenido } = response

      const task: ContentTask = {
        id: tarea.id,
        organization_id: tarea.organization_id,
        status: tarea.estado as ContentTask['status'],
        content_type: tarea.tipo_contenido,
        channel: tarea.canal,
        persona_id: tarea.persona_id,
        topic: tarea.tema,
        created_at: tarea.created_at,
        started_at: tarea.started_at ?? null,
        completed_at: tarea.completed_at ?? null,
        error_message: tarea.error_message ?? null,
        conversation_id: tarea.conversation_id ?? null,
        brief: null,
        keywords: null,
        word_count_target: null,
        include_research: null,
        research_query: null,
      }

      const pieces: ContentPiece[] = piezas_contenido.map((p) => ({
        id: p.id,
        task_id: p.task_id,
        persona_id: p.persona_id,
        title: p.title,
        body: p.body,
        excerpt: p.excerpt,
        cta: p.cta,
        status: p.status,
        current_version: p.current_version,
        word_count: p.word_count,
        created_at: p.created_at,
        updated_at: p.updated_at ?? p.created_at,
        sources: (p.sources ?? []).map((s) => ({
          id: s.id,
          title: s.title ?? '',
          url: s.url,
          domain: s.domain,
          snippet: s.snippet,
        })),
        research_summary: p.research_summary,
        research_key_findings: p.research_key_findings,
      }))

      return { task, pieces }
    },
    enabled: !!taskId,
    refetchInterval: (query) => {
      const data = query.state.data
      const isPendingOrRunning =
        data?.task?.status === 'pending' || data?.task?.status === 'running'
      return isPendingOrRunning ? 5000 : false
    },
  })
}

export function useContentTasksByConversation(conversationId: string | null) {
  const organizationId = useAppStore((state) => state.organizationId)

  return useQuery({
    queryKey: ['content_tasks_by_conversation', conversationId, organizationId],
    queryFn: async () => {
      if (!conversationId || !organizationId) return []

      const { data, error } = await supabase
        .from('content_tasks')
        .select('id, topic, status, content_type, channel, created_at')
        .eq('conversation_id', conversationId)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return (data ?? []) as Array<{
        id: string
        topic: string
        status: string
        content_type: string
        channel: string
        created_at: string
      }>
    },
    enabled: !!conversationId && !!organizationId,
  })
}

export function useContentVersions(contentId: string | null) {
  return useQuery({
    queryKey: ['content_versions', contentId],
    queryFn: async () => {
      if (!contentId) return []
      
      const { data, error } = await supabase
        .from('content_versions')
        .select('*')
        .eq('content_id', contentId)
        .order('version', { ascending: false })

      if (error) throw error
      return data
    },
    enabled: !!contentId,
  })
}
